// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Utils
import { getEMRErrorMessage } from "src/utils/emr/emrErrorHelper";

// API
import { deleteEMRHubConnection } from "../../api";

export const useDeleteEMRHubConnection = (
  clinicId: string | undefined,
  hubId: string | undefined
) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (connectionId: string) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return deleteEMRHubConnection(token, clinicId, connectionId);
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["emr-hub-connections", clinicId, hubId],
      });
      setSnackbarInfo({
        variant: "success",
        message: t("Connection deleted successfully!"),
      });
    },
    onError: (error) => {
      setSnackbarInfo({
        variant: "error",
        message: getEMRErrorMessage(
          error,
          t("Connection could not be deleted!"),
          t
        ),
      });
    },
  });
};
